import { naturalSortBy } from 'utils/list'

export const kubernetesTitles = {
  DEPLOYMENT: 'Deployment',
  DAEMONSET: 'DaemonSet',
  REPLICASET: 'ReplicaSet',
  STATEFULSET: 'StatefulSet',
  POD: 'Pod'
}

export const kubernetesKindColors = {
  // Custom, one off colors for graph "selected" colors of various types of objects
  DEPLOYMENT: '#6E40AA',
  DAEMONSET: '#1AC7C2',
  REPLICASET: '#23C386',
  STATEFULSET: '#407DE0',
  POD: '#ADF37B'
}

export const flattenKubernetesTargets = (targets = []) => {
  return targets.flatMap((cluster) => cluster?.objects ?? [])
}

export const isK8TargetSelected = (target, selectedObjects = new Set()) => {
  return selectedObjects.has(target.uid)
}

export const getTotalSelectedK8Clients = (selectedObjects) =>
  selectedObjects.size

export function getAllSelectedK8PodObjects(
  selectedObjects = new Set(),
  targetsByOwners = []
) {
  if (selectedObjects.length < 1 || targetsByOwners.length < 1) {
    return []
  }

  const selectedK8PODObjects = targetsByOwners.reduce((acc, owner) => {
    if (selectedObjects.has(owner.uid)) {
      // parent-less POD
      if (owner.target.kind === 'POD') {
        acc.push(owner.target)
        return acc
      }

      // add any pods this object contains
      if (owner.children.POD) {
        acc = acc.concat(owner.children.POD)
        return acc
      }
    }

    return acc
  }, [])

  return selectedK8PODObjects
}

export function getTotalSelectedK8Pods(
  selectedObjects = new Set(),
  targetsByOwners = []
) {
  return getAllSelectedK8PodObjects(selectedObjects, targetsByOwners).length
}

// FixMe: to be used when reducing the shown tags to only those available after an initial choice
// function isOwnerSelected(target, selectedTargetParams) {
//   const { ownerReferences = '' } = target
//
//   return Object.values(selectedTargetParams).includes(ownerReferences)
// }

export const formatKubernetesParams = (
  mappedTargetList /*selectedTargetParams*/
) => {
  // const emptyTargetParams = Object.values(selectedTargetParams).length === 0
  const out = naturalSortBy(mappedTargetList, (t) => t.name).reduce(
    ({ keyValueSets, labelKeys }, parent) => {
      // FixMe: to be used when reducing the shown tags to only those available after an initial choice
      // if (!emptyTargetParams) {
      //   if (!isOwnerSelected(target, selectedTargetParams)) {
      //     return { keyValueSets, labelKeys }
      //   }
      // }

      const { uid, kind, name } = parent.target
      const targetName = name || uid

      if (!labelKeys[kind]) {
        labelKeys[kind] = []
      }

      //add key-value to the set
      keyValueSets.add(`${kubernetesTitles[kind]}:${targetName}`)
      labelKeys[kind].push(parent)

      return { keyValueSets, labelKeys }
    },
    {
      keyValueSets: new Set(),
      labelKeys: {}
    }
  )

  return {
    availableTargetParams: out.keyValueSets,
    labelKeys: Object.fromEntries(
      Object.entries(out.labelKeys).map(([kind, targets]) => [kind, targets])
    )
  }
}

export const sortKubernetesObjectParams = () => {
  const defaults = [
    'DEPLOYMENT',
    'DAEMONSET',
    'REPLICASET',
    'STATEFULSET',
    'POD'
  ]
  const displayed = defaults
  return { displayed, others: [] }
}

export const getListByK8Property = (targets, propertyName) => {
  return targets.reduce((accum, target) => {
    if (target[propertyName]) accum.add(target[propertyName])
    return accum
  }, new Set())
}

export function groupKubernetesObjects(targets) {
  return targets.reduce((parents, target) => {
    const { ownerReferences } = target

    if (ownerReferences) {
      if (!parents[ownerReferences]) {
        parents[ownerReferences] = []
      }

      parents[ownerReferences].push(target)
    }

    return parents
  }, {})
}

export const getTargetAndDecendents = (target, targetParentMap) => {
  function getChildren(group = []) {
    return group.flatMap((groupChild) => {
      return [groupChild].concat(getChildren(targetParentMap[groupChild.uid]))
    })
  }

  return getChildren([target])
}

export const unhealthyPod = (maybePod) =>
  maybePod?.kind === 'POD' && maybePod?.podPhase !== 'Running'

export const generateKubernetesSelectionOptions = (targets) => {
  const targetParentMap = groupKubernetesObjects(targets)

  const targetsByOwners = targets
    .filter((t) => !t.ownerReferences)
    .map((t) => ({
      target: t,
      name: t.name || t.uid,
      uid: t.uid,
      children: getTargetAndDecendents(t, targetParentMap).reduce(
        (accum, child) => {
          // ignore the target itself
          if (child.uid === t.uid) {
            return accum
          }

          if (!accum[child.kind]) {
            accum[child.kind] = []
          }

          accum[child.kind].push(child)
          return accum
        },
        {}
      )
    }))
  return targetsByOwners
}

export function getSelectedNamespace(
  selectedClusterId,
  k8sClients,
  forceSelectionLimit,
  selectedObjects
) {
  const isTargetSelected = (client) => {
    return (
      client.clientId === selectedClusterId ||
      client.assetId === selectedClusterId
    )
  }
  const selectedCluster = k8sClients.find((client) => isTargetSelected(client))

  if (Array.isArray(selectedObjects)) {
    // if selected objects is listed and they all have the same namespace use that one.
    if (new Set(selectedObjects.map((o) => o.namespace)).size === 1) {
      if (selectedObjects[0]?.clusterId === selectedCluster.clusterId) {
        return selectedObjects[0].namespace
      }
    }
  }

  const selectedClusterObjects = selectedCluster?.objects ?? []

  const numberOfObjects = selectedClusterObjects.length

  const namespaceSet = new Set(
    selectedClusterObjects.map((k8Object) => k8Object.namespace)
  )

  const tempSelectedNamespace =
    namespaceSet.size === 1 ? selectedClusterObjects[0].namespace : undefined

  if (
    tempSelectedNamespace &&
    selectedClusterObjects
      .filter((k8Object) => isTargetSelected(k8Object))
      .some((k8Object) => k8Object.namespace === tempSelectedNamespace)
  ) {
    return tempSelectedNamespace
  }

  return numberOfObjects > forceSelectionLimit
    ? Array.from(
        new Set(selectedClusterObjects.map((o) => o.namespace))
      ).sort()[0]
    : 'All'
}

export function getSelectedKubernetesTargets(
  targets,
  { selectedNamespace, selectedObjects }
) {
  return targets.filter((t) => {
    const isTargetSelected = selectedObjects.has(t.uid)

    if (isTargetSelected) {
      if (selectedNamespace && selectedNamespace.toLowerCase() !== 'all') {
        return t.namespace === selectedNamespace
      }
    }

    return isTargetSelected
  })
}

export function getK8sObjectsFromTargets(k8sObjects = [], clients = []) {
  const k8sTargetIds = new Set(k8sObjects.map((k8sObject) => k8sObject.uid))
  return clients
    .map((client) => client.objects.filter((obj) => k8sTargetIds.has(obj.uid)))
    .reduce((objects, clientObjects) => [...objects, ...clientObjects], [])
}
